import React from "react";
import styles from "./LinkListModule.module.scss";
import { LinkListOptionsType } from "@/types/ModuleOptionsType";
import { columnSize } from "../../utils/layout/columnSize";
import { Modul_Linkliste } from "../../wp/types/acf-module-types";
import { useSublinesVisibility } from "../../hooks/useSublinesOverlinesVisibility";
import { checkModuleIsHidden } from "@/utils/checkModuleIsHidden";
import { Headline } from "@/components/Headline/Headline";
import { Section } from "@/components/Section/Section";
import { Button } from "@/components/Button/Button";
import {
  Grid,
  GridContent,
  GridItem,
} from "@aleksundshantu/aunds-utils-react/primitives/grid";
import { useRouter } from "next/router";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { fetchLinkListItems } from "@/getData/fetchLinkListItems";
import { LinkListBox, LinkListBoxElementType } from "./LinkListBox/LinkListBox";
import { GridPagination } from "@/components/Pagination/GridPagination";

export type LinkListModuleType = Modul_Linkliste;

interface LinkListModuleProps {
  data: LinkListModuleType;
  linkListOptions: LinkListOptionsType;
  isNobleElements: boolean;
}

const LinkListModule = ({
  linkListOptions,
  data,
  isNobleElements,
}: LinkListModuleProps) => {
  {
    /* jscpd:ignore-start */
  }
  const content = data.linklist;

  const {
    headline,
    headline_html_tag,
    overline,
    subline,
    type: _type,
  } = content;

  const {
    id,
    background_color_class,
    section_margin_class,
    section_padding_class,
    module_width,
    headline_text_align_class,
    text_align_class,
    toggle_cardbox,
    trenner_anzeigen,
    icon_align_class,
    flex_direction_class,
    row_direction_class,
    posts_per_page,
  } = data.layout;

  const globalShowBox = linkListOptions?.box?.show_card_box ?? false;
  const showCardbox = globalShowBox ? toggle_cardbox : false;

  const { show_overline_subline, columns } = linkListOptions ?? {
    show_overline_subline: "none",
    columns: { breakpoint: "tablet", number_of_cols: 12 },
  };

  const { showOverline, showSubline } = useSublinesVisibility(
    show_overline_subline ?? "none",
  );

  const { locale, defaultLocale } = useRouter();
  const { getLabel, getIcon } = useGlobalContext();

  const fallbackIconMap: Record<string, { noble: string; elements: string }> = {
    jobs: { noble: "liJobNoble", elements: "liJobElements" },
    events: { noble: "liEventNobleBC", elements: "liEventNobleElements" },
    insights: {
      noble: "liInsightsNobleBC",
      elements: "liInsightsNobleElements",
    },
    press: { noble: "liPresseNobleBC", elements: "liPresseNobleElements" },
  };

  const iconKeys = fallbackIconMap[_type];
  const fallbackIconUrl = iconKeys
    ? getIcon(isNobleElements ? iconKeys.elements : iconKeys.noble)
    : undefined;

  //! check if module is hidden
  const isHidden = checkModuleIsHidden(data.settings);
  if (isHidden) {
    return null;
  }

  const columnSizes =
    module_width !== "normal-frame"
      ? columns
        ? columnSize(columns)
        : "is-12"
      : "is-12";

  const labels = {
    jobsBtn: getLabel("jobs_link_list_btn_label", "Zum Job"),
    insightsBtn: getLabel("insights_link_list_btn_label", "Mehr erfahren"),
    pressBtn: getLabel("press_link_list_btn_label", "Mehr erfahren"),
    teamBtn: getLabel("team_link_list_btn_label", "Mehr erfahren"),
    eventsBtn: getLabel("events_link_list_btn_label", "Mehr erfahren"),
  };

  const itemWidth = "100%";

  const moduleLink = content.link;

  const linkListJSX = (
    <div className="columns is-multiline is-centered">
      <div className={`column is-12`}>
        <Headline
          headline={headline}
          headline_html_tag={headline_html_tag}
          overline={showOverline ? overline : ""}
          subline={showSubline ? subline : ""}
          extraClass={headline_text_align_class}
          wrapInSection={module_width === "frameless"}
          disableAnimation
        />
      </div>

      <div className={`column is-12`}>
        <Grid
          postsPerPage={posts_per_page}
          fetchItems={({ page, postsPerPage, offset }) =>
            fetchLinkListItems({
              page,
              postsPerPage,
              offset,
              locale,
              defaultLocale,
              module: data,
              labels,
              isNobleElements,
            })
          }
          renderLoading={{ numberOfItemsWhileLoading: 3 }}
        >
          {(items, loading) => (
            <>
              <GridContent gap="2rem">
                {items.map((el, i) => (
                  <GridItem itemWidth={itemWidth} key={i}>
                    <LinkListBox
                      element={el as LinkListBoxElementType}
                      infoBoxOptions={linkListOptions}
                      show_cardbox={showCardbox}
                      show_divider={trenner_anzeigen}
                      heading_level={headline ? 3 : 2}
                      layoutOptions={{
                        title_style: "bold",
                        icon_align_class,
                        flex_direction_class,
                        row_direction_class,
                        text_align_class,
                      }}
                      boxType={_type === "team" ? "team" : "default"}
                      skeleton={loading}
                      fallbackIconUrl={fallbackIconUrl}
                    />
                  </GridItem>
                ))}
              </GridContent>
              <GridPagination />
            </>
          )}
        </Grid>
      </div>

      {moduleLink && typeof moduleLink !== "string" && moduleLink.url && (
        <div className={`column is-12 is-flex is-justify-content-center`}>
          <Button
            role="button"
            type={"secondary"}
            href={moduleLink.url}
            target={moduleLink.target === "" ? "_self" : moduleLink.target}
            aria-label={moduleLink.aria_label}
          >
            {moduleLink.title}
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <Section
      id={id}
      className={`link-list ${styles["link-list-mod"]} ${background_color_class === "has-gradient-bg" && !showCardbox ? styles["is-text-white"] : ""} ${background_color_class} ${section_padding_class} ${section_margin_class}`}
      disableColumns
      frameless={module_width === "frameless"}
    >
      {module_width === "narrow-frame" ? (
        <div className="columns is-centered">
          <div className={`column ${columnSizes} is-centered`}>
            {linkListJSX}
          </div>
        </div>
      ) : (
        linkListJSX
      )}
    </Section>
  );
  {
    /* jscpd:ignore-end */
  }
};

export default LinkListModule;
